import type { ReactNode } from 'react'

import { FvLinkButton, type IconName } from '@fv/client-components'
import type { NotificationType } from '@fv/models'

import type { Notification } from './queries'

type DetailsComponentProps = {
  notification: Notification
}
type NotificationDetailsComponent = (props: DetailsComponentProps) => ReactNode

const ExportDetails = ({ notification }: DetailsComponentProps) => {
  return (
    <div>
      <p className="mb-0 leading-6">
        Your export has completed. You can
        <FvLinkButton
          href={notification.link}
          target="_blank"
          theme="underlined"
          className="ml-1"
        >
          download it here.
        </FvLinkButton>
      </p>
    </div>
  )
}

const DocumentBatchDetails = ({ notification }: DetailsComponentProps) => {
  return (
    <div>
      <p className="mb-0 leading-6">
        <FvLinkButton
          href={notification.link}
          target="_blank"
          theme="default"
          className="ml-1"
          icon="download"
        >
          Download your paperwork
        </FvLinkButton>
      </p>
    </div>
  )
}

const LoadUploadCompletedDetails = ({
  notification,
}: DetailsComponentProps) => {
  return (
    <div>
      <p className="mb-0 leading-6">
        {notification.text && <>{notification.text}</>}
        {!!notification.link && (
          <FvLinkButton
            to={notification.link}
            theme="default"
            className="ml-1"
            icon="arrow-to-right"
          >
            View shipments
          </FvLinkButton>
        )}
      </p>
    </div>
  )
}
type RegistryEntry = {
  component?: NotificationDetailsComponent
  title: string
  icon: IconName
  iconColor?: string
  bgColor?: string
}

export const notificationRegistry: Record<NotificationType, RegistryEntry> = {
  export: {
    component: ExportDetails,
    title: 'Export complete',
    icon: 'download',
  },
  'document-batch': {
    component: DocumentBatchDetails,
    title: 'Paperwork generated',
    icon: 'download',
  },
  'tracking-change': {
    title: 'Shipment updated',
    icon: 'truck-fast',
  },
  'booking-error': {
    title: 'Booking Error',
    icon: 'exclamation-triangle',
    iconColor: 'text-fv-red',
    bgColor: 'bg-red-50',
  },
  'missed-pickup': {
    title: 'Missed Pickup',
    icon: 'exclamation-triangle',
    iconColor: 'text-fv-red',
    bgColor: 'bg-red-50',
  },
  rejection: {
    title: 'Shipment rejected',
    icon: 'exclamation-triangle',
    iconColor: 'text-fv-red',
    bgColor: 'bg-red-50',
  },
  'vendor-created': {
    title: 'Vendor created shipment',
    icon: 'warehouse',
    bgColor: 'bg-fv-green-50',
  },
  'vendor-booked': {
    title: 'Vendor booked shipment',
    icon: 'warehouse',
    bgColor: 'bg-fv-green-50',
  },
  'doc-center:invalid-email': {
    title: 'Document email blocked',
    icon: 'ban',
    iconColor: 'text-fv-red',
    bgColor: 'bg-red-50',
  },
  'doc-center:email-finished': {
    title: 'Document email processed',
    icon: 'file-plus',
  },
  'load-upload:completed': {
    component: LoadUploadCompletedDetails,
    title: 'Shipment upload completed',
    icon: 'cloud-upload',
  },
  'route-guide:exhausted': {
    title: 'Route guide exhausted',
    icon: 'ban',
    iconColor: 'text-fv-orange',
    bgColor: 'bg-fv-orange-50',
  },
}

export const getNotificationTitle = (notification: Notification) =>
  notification.title ??
  notificationRegistry[notification.notificationType]?.title ??
  ''
